import axiosInstance from './axiosConfig';
import ApiConfig from './config';

/**
 * Fetch activity log entries.
 *
 * @param {Object}   options
 * @param {'Business'|'Terminal'} options.entityType - Which entity the log belongs to
 * @param {number|string} options.entityId  - ID of the business / terminal
 * @param {number[]} [options.activityTypes] - Filter by ActivityType enum values.
 *                                             If empty/omitted, all activityTypes are sent.
 * @param {number}   [options.page=1]        - Page number (1-based)
 * @param {number}   [options.pageSize=20]   - Items per page
 * @returns {Promise<{ data: object[], items: object[], totalPages: number|null, totalCount: number|null, version: number }>}
 */
export const getActivityLog = async ({
  entityType = 'Business',
  entityId,
  activityTypes = [],
  page = 1,
  pageSize = 20,
}) => {
  try {
    // ---- Pick the correct endpoint based on entityType ----
    const endpoint =
      String(entityType).toLowerCase() === 'terminal'
        ? ApiConfig.ENDPOINTS.GET_TERMINAL_ACTIVITY_LOG
        : ApiConfig.ENDPOINTS.GET_BUSINESS_ACTIVITY_LOG;

    const params =
      String(entityType).toLowerCase() === 'terminal'
        ? { tId: entityId }
        : { businessId: entityId };
    // ---- Resolve the types array ----
    // If the caller provided specific types, use them.
    // Otherwise send ALL known activity types.
    const ALL_ACTIVITY_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const typesToSend =
      Array.isArray(activityTypes) && activityTypes.length > 0
        ? activityTypes
        : ALL_ACTIVITY_TYPES;

    const body = {
      page,
      pageSize,
      // Only request the count on the first page — subsequent pages
      // skip the (expensive) count query on the backend.
      includeCount: page === 1,
      // Always send as `types` array, per API contract.
      types: typesToSend,
    };

    const response = await axiosInstance.put(endpoint, body, { params });

    const payload = response.data;

    // ---- Normalize the response into a consistent shape ----
    let items = [];
    if (Array.isArray(payload)) {
      items = payload;
    } else if (payload && Array.isArray(payload.data)) {
      items = payload.data;
    } else if (payload && Array.isArray(payload.items)) {
      items = payload.items;
    } else if (payload && typeof payload === 'object') {
      items = [payload];
    }

    return {
      data: items,
      items,
      // When includeCount=false the backend won't return totalPages —
      // return null so the caller can preserve its previous value.
      totalPages: payload?.totalPages ?? null,
      totalCount: payload?.totalCount ?? null,
      version: payload?.version ?? 0,
    };
  } catch (error) {
    throw error;
  }
};